import { useState, type ReactNode } from 'react'
import { useReducedMotion } from '../../hooks/useReducedMotion'
import { cn } from '../../lib/cn'

export interface FaqItem {
  question: string
  answer: ReactNode
}

export interface FaqSectionProps {
  headline: string
  items: FaqItem[]
  onItemExpand?: (question: string) => void
  className?: string
}

interface FaqAccordionItemProps {
  index: number
  question: string
  answer: ReactNode
  isExpanded: boolean
  onToggle: () => void
  reducedMotion: boolean
}

const FaqAccordionItem = ({
  index,
  question,
  answer,
  isExpanded,
  onToggle,
  reducedMotion,
}: FaqAccordionItemProps) => (
  <div className='flex w-full max-w-[800px] flex-col border-t border-white/20 py-5'>
    <button
      type='button'
      className='flex w-full cursor-pointer items-center gap-5 border-none bg-transparent p-0'
      onClick={onToggle}
      aria-expanded={isExpanded}
      aria-controls={`faq-answer-${index}`}
    >
      <span className='flex-1 text-left text-[14px] leading-[1.1] tracking-[0.56px] text-white'>
        {question}
      </span>
      <span className='text-white'>
        {isExpanded ? (
          <svg
            xmlns='http://www.w3.org/2000/svg'
            viewBox='0 0 24 24'
            fill='none'
            aria-hidden='true'
            className='w-5 h-2.5'
          >
            <path
              fill='currentColor'
              fillRule='evenodd'
              d='M21.675 17.678a1.12 1.12 0 0 1-1.572 0L12 9.656l-8.103 8.022c-.434.43-1.138.43-1.572 0a1.09 1.09 0 0 1 0-1.556l8.89-8.8a1.12 1.12 0 0 1 1.57 0l8.89 8.8c.433.43.433 1.126 0 1.556'
              clipRule='evenodd'
            />
          </svg>
        ) : (
          <svg
            xmlns='http://www.w3.org/2000/svg'
            viewBox='0 0 24 24'
            fill='none'
            aria-hidden='true'
            className='w-5 h-2.5'
          >
            <path
              fill='currentColor'
              fillRule='evenodd'
              d='M2.325 7.322a1.12 1.12 0 0 1 1.572 0L12 15.344l8.103-8.022a1.12 1.12 0 0 1 1.572 0c.433.43.433 1.126 0 1.556l-8.89 8.8a1.12 1.12 0 0 1-1.57 0l-8.89-8.8a1.09 1.09 0 0 1 0-1.556'
              clipRule='evenodd'
            />
          </svg>
        )}
      </span>
    </button>

    <div
      id={`faq-answer-${index}`}
      className={cn(
        'grid overflow-hidden',
        !reducedMotion &&
          'transition-[grid-template-rows] duration-300 ease-in-out',
        isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
      )}
    >
      <div className='overflow-hidden'>
        <div className='pb-[10px] pr-10 pt-5'>
          <p className='m-0 whitespace-pre-line text-[14px] leading-[1.2] tracking-[0.56px] text-white'>
            {answer}
          </p>
        </div>
      </div>
    </div>
  </div>
)

export const FaqSection = ({
  headline,
  items,
  onItemExpand,
  className,
}: FaqSectionProps) => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(0)
  const reducedMotion = useReducedMotion()

  const handleToggle = (index: number) => {
    const isExpanding = expandedIndex !== index
    if (isExpanding && onItemExpand) {
      onItemExpand(items[index].question)
    }
    setExpandedIndex(isExpanding ? index : null)
  }

  return (
    <section
      id='faq'
      data-section='faq'
      className={cn(
        'relative z-[1] flex flex-col items-center bg-ja-faq px-[18.5px] pb-[60px] pt-[10px] md:px-[10px]',
        className,
      )}
    >
      <div className='w-full max-w-[800px] border-t border-white/20 py-5'>
        <h2 className='m-0 text-body-1 leading-body-1 font-semibold text-white md:text-title-3 md:leading-title-3'>
          {headline}
        </h2>
      </div>

      {items.map((item, index) => (
        <FaqAccordionItem
          key={index}
          index={index}
          question={item.question}
          answer={item.answer}
          isExpanded={expandedIndex === index}
          onToggle={() => handleToggle(index)}
          reducedMotion={reducedMotion}
        />
      ))}

      <div className='w-full max-w-[800px] border-t border-white/20' />
    </section>
  )
}
